import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { jwtDecode } from 'jwt-decode'; // Assuming named import works now

// Define your API endpoint URL
const COMMENTS_API_URL = 'https://5cju2zryeeq7yn4ewjfme3iaey0mrkqa.lambda-url.us-east-1.on.aws/'; // *** REPLACE WITH YOUR ACTUAL API ENDPOINT ***

// Define the constant for the specific topic key (assuming you added this earlier)
const MODELS_TOPIC_KEY = "You Like Models?!?!?!?!";


// CommentSection Component - Accepts isLoggedIn prop
function CommentSection({ imageId, onCommentSubmit, existingComments, isLoggedIn }) {
  const [comment, setComment] = useState('');
  const textareaId = `comment-input-${imageId}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (comment.trim()) {
      const success = await onCommentSubmit(comment);
      if (success) {
         setComment('');
      }
    }
  };

  // Sort comments by datetime before rendering
  // Create a copy before sorting to avoid mutating the prop directly
  const sortedComments = [...existingComments].sort((a, b) => new Date(a.datetime) - new Date(b.datetime));

  // Debug log to see what comments this specific CommentSection receives
  // console.log(`CommentSection for imageId: ${imageId} received ${existingComments.length} comments.`);
  // console.log("Received comments for imageId:", imageId, existingComments); // Uncomment for detailed view


  return (
    <div className="comment-section">
      <h4>Comments</h4>
      <ul className="comment-list">
        {sortedComments.map((commentObj) => (
          // Use comment ID as key if available and unique, fallback to index if not
          <li key={commentObj.id || commentObj.datetime || Math.random()}>
            {/* Display the user's name and the comment text */}
            <strong>{commentObj.user || 'Anonymous'}:</strong> {commentObj.comment}
             {commentObj.datetime && (
                <span className="comment-timestamp">
                   {' '} - {new Date(commentObj.datetime).toLocaleString()}
                </span>
             )}
          </li>
        ))}
      </ul>
      {/* Render comment form ONLY if logged in */}
      {isLoggedIn && ( // <--- This is the line using isLoggedIn
        <form onSubmit={handleSubmit} className="comment-form">
          <label htmlFor={textareaId} className="visually-hidden">Add a comment for {imageId}</label>
          <textarea
            id={textareaId}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment..."
            rows="3"
          />
          <button type="submit">Submit</button>
        </form>
      )}
    </div>
  );
}


function App() {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [likes, setLikes] = useState({});
  // State to hold ALL comments as a flat array
  const [allComments, setAllComments] = useState([]);

  const [lastUpdated, setLastUpdated] = useState("June 4th, 2025 (Interior updates)");
  const [selectedImage, setSelectedImage] = useState(null);
  const [count, setCount] = useState(null);
  const [counterElements, setCounterElements] = useState([]);

  // Refs for DOM elements
  const fullSizeImageRef = useRef(null);
  const signInDivRef = useRef(null);


  // --- Mobile Responsiveness State & Effect ---
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobileCheck = window.innerWidth < 768;
      setIsMobile(mobileCheck);
      if (!mobileCheck) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // --- End Mobile Responsiveness ---


  // --- Counter Logic ---
  const getCount = async () => {
    try {
      const response = await fetch(`https://kkjbpranvouupwd5mx7dxcbzay0ncmrm.lambda-url.us-east-1.on.aws/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCount(data.count);
    } catch (error) {
      console.error('Error fetching counter data:', error);
    }
  };
  useEffect(() => { getCount(); }, []);

  useEffect(() => {
    const elements = document.querySelectorAll('.cntr');
    setCounterElements(Array.from(elements));
  }, []);

  useEffect(() => {
    if (count !== null && count !== undefined && counterElements.length > 0) {
      const str = count.toString().padStart(6, '0');
      counterElements.forEach((element, index) => {
        if (str[index] && element.innerHTML !== str[index]) {
          element.innerHTML = str[index];
        }
      });
    }
  }, [count, counterElements]);
  // --- End Counter Logic ---


  // --- Main Logo Aspect Ratio Effect ---
   useEffect(() => {
    const img = fullSizeImageRef.current;
    if (!selectedTopic && img) {
      const handleLoad = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
           const aspectRatio = img.naturalWidth / img.naturalHeight;
           const container = img.parentElement;
           if (container && container.offsetWidth > 0 && aspectRatio > 0) {
             const calculatedHeight = container.offsetWidth / aspectRatio;
             container.style.height = `${calculatedHeight}px`;
           }
        }
      };
      if (img.parentElement) { img.parentElement.style.height = 'auto'; }
      if (img.complete && img.naturalWidth > 0) { handleLoad(); } else { img.addEventListener('load', handleLoad); }
      return () => {
         img.removeEventListener('load', handleLoad);
         if(img.parentElement) img.parentElement.style.height = 'auto';
      };
    } else if (img?.parentElement) {
         img.parentElement.style.height = 'auto';
    }
  }, [selectedTopic]);
  // --- End Main Logo Aspect Ratio Effect ---


  // --- Google OAuth2 Logic ---

  const handleGoogleLoginResponse = (response) => {
    const userObject = jwtDecode(response.credential);
    setUserProfile(userObject);
    setIsLoggedIn(true);
    // No need to fetch comments here anymore, useEffect will handle it based on isLoggedIn
  };

  useEffect(() => {
    /* global google */
    if (window.google) {
        google.accounts.id.initialize({
            client_id: "842947203617-u37rtdhah67vfgols5ni3gsmkv1g1pph.apps.googleusercontent.com", // <<< REPLACE WITH YOUR CLIENT ID
            callback: handleGoogleLoginResponse
        });
        google.accounts.id.renderButton(
            document.getElementById("signInDiv"),
            { theme: "outline", size: "large" }
        );
    } else {
        console.error("Google Identity Services script not loaded.");
    }
}, []);

  const handleLogout = () => {
      setUserProfile(null);
      setIsLoggedIn(false);
      console.log("User logged out.");
      // Optionally clear comments on logout if you don't want logged out users to see them
      // setAllComments([]);
  };
  // --- End Google OAuth2 Logic ---


  // --- Comment API Logic ---

  // Function to fetch all comments from the API
  const fetchComments = async () => {
    console.log("Fetching all comments from API...");
    try {
      // Use POST with empty body to trigger fetch all logic in Lambda
      const response = await fetch(COMMENTS_API_URL,{
        method: 'POST', // Use POST with empty body
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}) // Send an empty object as the body
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // *** EXPECTING { AllComments: [...] } structure from POST with empty body ***
      // If API is configured to return { CommentsByImage: {...} } for GET, adjust fetch method/body accordingly.
      // Assuming POST with empty body returns { AllComments: [...] } based on previous Lambda update.
      if (data && Array.isArray(data.AllComments)) {
         setAllComments(data.AllComments); // Update state with the flat array
         console.log("Comments fetched and flattened:", data.AllComments.length);
      } else {
          // Handle cases where the expected structure is not returned
          console.error("API response format incorrect for fetching comments:", data);
          setAllComments([]); // Clear comments or handle appropriately
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      // Optionally show an error message to the user
    }
  };

  // *** Fetch comments when the component mounts (page load/refresh) ***
  useEffect(() => {
    fetchComments();
  }, []); // Empty dependency array means this runs once on mount


  // Function to submit a new comment to the API
  const postComment = async (imageId, commentText) => {
    if (!isLoggedIn || !userProfile || !userProfile.name) {
        console.warn("Attempted to post comment without being logged in or missing user info.");
        return false;
    }

    const commentData = {
      imageId: imageId,
      user: userProfile.name,
      comment: commentText,
    };

    console.log("Posting comment data:", commentData);

    try {
      const response = await fetch(COMMENTS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add Authorization header if your Lambda requires it
          // 'Authorization': `Bearer ${userProfile.id_token}` // Example if using ID token
        },
        body: JSON.stringify(commentData),
      });

      if (!response.ok) {
         let errorMessage = `HTTP error! status: ${response.status}`;
         try {
             const errorBody = await response.json();
             if (errorBody && errorBody.message) {
                 errorMessage = `API Error: ${errorBody.message} (Status: ${response.status})`;
             } else if (errorBody) {
                  errorMessage = `API Error: ${JSON.stringify(errorBody)} (Status: ${response.status})`;
             }
         } catch (parseError) {
              console.error("Failed to parse error body:", parseError);
         }
        console.error('Error posting comment:', errorMessage);
        // Optionally show error to user
        return false;
      }

      // *** API POST response should return the updated flat list under 'AllComments' ***
      const updatedCommentsData = await response.json();
      if (updatedCommentsData && Array.isArray(updatedCommentsData.AllComments)) {
         setAllComments(updatedCommentsData.AllComments); // Update state with the flat array
         console.log("Comment posted successfully. Updated flattened comments count:", updatedCommentsData.AllComments.length);
         return true; // Indicate success
      } else {
         console.error("API POST response format incorrect after posting comment:", updatedCommentsData);
         // If format is wrong but status was OK, maybe refetch comments?
         fetchComments(); // Refetch all comments as a fallback
         return true; // Still indicate success if API status was OK but format unexpected
      }

    } catch (error) {
      console.error('Error posting comment:', error);
      // Optionally show an error message to the user
      return false; // Indicate failure
    }
  };

  // handleCommentSubmit in App now calls postComment API function
  const handleAppCommentSubmit = async (imageId, commentText) => {
      // postComment returns true on success (or successful refetch), false on failure
      const success = await postComment(imageId, commentText);
      return success; // Pass success status back to CommentSection
  };
  // --- End Comment API Logic ---


  const handleTopicClick = (topic) => {
    setSelectedTopic(topic);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const handleLike = (imageId) => {
    setLikes((prevLikes) => ({
      ...prevLikes,
      [imageId]: (prevLikes[imageId] || 0) + 1,
    }));
  };

  const handleImageClick = (imageUrl) => {
     const imageExistsInMilestones = Object.values(milestones).some(
         milestone => milestone.images && milestone.images.some(img => img.url === imageUrl)
     );
     if (imageExistsInMilestones) {
        setSelectedImage(imageUrl);
     } else {
         console.log("Clicked a non-milestone image or main logo, not opening modal.");
     }
  };


  const closeModal = () => {
      setSelectedImage(null);
  };

   const handleDownload = () => {
    if (selectedTopic === MODELS_TOPIC_KEY && milestones[selectedTopic]?.link) {
        const fileUrl = milestones[selectedTopic].link;
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileUrl.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else {
        console.error("Download requested for a topic without a link or wrong topic.");
    }
   };


  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Milestone data (ensure MODELS_TOPIC_KEY is used correctly)
  const milestones = {
    "Concept & Design": {
      images: [
        { url: "concept.jpg", caption: "Gemini Rendering", id: "concept" },
        { url: "original.jpg", caption: "What we started with", id: "original" },
      ],
      text: "The initial phase involved brainstorming and creating digital renders of the final design.",
    },
     "The Gentle Way...":{ /* ... data ... */ images: [ {url: "failed1.jpg", caption: "Trying to save the front end", id: "failed1"}, {url: "failed2.jpg", caption: "Something to weld the cross member to that bolts in...", id: "failed2"}, {url: "failed3.jpg", caption: "Working with the new cross memeber", id: "failed3"}, {url: "failed4.jpg", caption: "Getting somewhere, brakes look damn good", id: "failed4"}, {url: "failed5.jpg", caption: "I mean really, that looks pretty sweet", id: "failed5"}, {url: "failed6.jpg", caption: "Can we fit a ford 9 \" where the old axle was?", id: "failed6"}, {url: "failed7.jpg", caption: "This is looking too hard to be honest", id: "failed7"}, ] },
    "The Harder Way...":{ /* ... data ... */ images: [ {url: "frame1.jpg", caption: "So I had this diagram and thought \" let's make a chassis \"", id: "frame1"}, {url: "frame2.jpg", caption: "So I began buying and cutting steel", id: "frame2"}, {url: "frame3.jpg", caption: "Yes the \"frame table\"  is leveled", id: "frame3"}, {url: "frame4.jpg", caption: "And then ...", id: "frame4"}, {url: "frame5.jpg", caption: "Still going", id: "frame5"}, {url: "frame6.jpg", caption: "C'mon that looks like a real chassis right?", id: "frame6"}, {url: "frame7.jpg", caption: "Got a professional to finish weld the chassis", id: "frame7"}, {url: "frame8.jpg", caption: "With the front suspension, brakes, and tires", id: "frame8"}, {url: "frame9.jpg", caption: "More of the same because it's bad ass looking", id: "frame9"}, {url: "frame10.jpg", caption: "Now to ge the rear axle in this mug", id: "frame10"}, {url: "frame11.jpg", caption: "And there we go", id: "frame11"}, {url: "frame12.jpg", caption: "Sweet, am I right?  I hope the measurements match the oirginal Vovlo schematic from the first pic ;-)", id: "frame12"},  {url: "frame13.jpg", caption: "A nice pic with some weldable primer on it", id: "frame13"}, {url: "frame14.jpg", caption: "Smoooooooth", id: "frame14"} ] },
    "Chassis + Unibody = $#%^&*?":{ /* ... data ... */ images: [ {url: "body1.jpg", caption: "Some night time prep for the big CUT tomorrow", id: "body1"}, {url: "body2.jpg", caption: "Gotta brace up the car if you're cutting it up", id: "body2"}, {url: "body3.jpg", caption: "Welp, no turning back", id: "body3"}, {url: "body4.jpg", caption: "Still didn't know how much dents and bondo the body had in it yet", id: "body4"}, {url: "body5.jpg", caption: "A Little frame/body forshadowing", id: "body5"}, {url: "body6.jpg", caption: "You can almost see it", id: "body6"}, {url: "body7.jpg", caption: "And... they are together", id: "body7"}, {url: "body8.jpg", caption: "Plenty of room for a V8", id: "body8"}, {url: "body9.jpg", caption: "Not bad looking", id: "body9"}, {url: "body10.jpg", caption: "Look Ma, no floors", id: "body10"}, {url: "body11.jpg", caption: "And no engine mounts", id: "body11"}, {url: "body12.jpg", caption: "Where we are going, we don't need doors.  Oh wait, this ain't a stupid Jeep! ", id: "body12"}, {url: "body13.jpg", caption: "Okay, I see you there.", id: "body13"}, {url: "body14.jpg", caption: "Front support", id: "body14"}, {url: "body15.jpg", caption: "Getting some structure back in the front", id: "body15"}, {url: "body16.jpg", caption: "And that's pretty good to go", id: "body16"} ] },
    "Motor and Stuff": { /* ... data ... */ images: [ {url: "engine1.jpg", caption: "Okay, she needs a new coat of paint.  Bondo and paint will make it what it ain't. I mean...", id: "engine1"}, {url: "engine2.jpg", caption: "I like the intake but still not sure.  It's tall", id: "engine2"}, {url: "engine3.jpg", caption: "Fuel tank in and plummed to the engine", id: "engine3"}, {url: "engine4.jpg", caption: "Engine in! ", id: "engine4"}, {url: "engine5.jpg", caption: "See how tall?", id: "engine5"}, {url: "engine6.jpg", caption: "Custom driveshaft balanced and installed", id: "engine6"}, {url: "engine7.jpg", caption: "Nice ass", id: "engine7"}, {url: "engine8.jpg", caption: "A little tail pipe action", id: "engine8"}, {url: "engine9.jpg", caption: "Getting the pipe over the axel. I considered under, but then grew q brain", id: "engine9"}, {url: "engine10.jpg", caption: "Coming off the headers and into the X-pipe.  This was harder than I thought it would be.", id: "engine10"}, {url: "engine11.jpg", caption: "The X-Pipe", id: "engine11"}, {url: "engine12.jpg", caption: "From the headers to the X-pipe", id: "engine12"} ] },
    "Interior Metal Work":{ /* ... data ... */ images: [ {url: "interior1.jpg", caption: "T56 6 speed with the original volvo shifter knob", id: "interior1"}, {url: "interior2.jpg", caption: "These are not fun to play with", id: "interior2"}, {url: "interior3.jpg", caption: "Original tunnel I made vs. the new one to ensure there was room for seats", id: "interior3"}, {url: "interior4.jpg", caption: "test fit", id: "interior4"}, {url: "interior5.jpg", caption: "Had to make the trans tunnel in two parts", id: "interior5"}, {url: "interior6.jpg", caption: "Seat fits AND the cleanup work around the shifter", id: "interior6"}, {url: "interior7.jpg", caption: "New rear tubs, new rear seat back (separates interior from trunk), and rear seat area", id: "interior7"},{url:"interior8.jpg",caption: "Raised the steering mount",id: "interior8"},{url:"interior9.jpg", caption:"This looks much better and is more comfortable", id: "interior9"} ] },
    "What's Next":{ /* ... data ... */ images: [{url: "whatsnext.jpg", caption: "", id: "final1"}], text: "Finsish the interior Metal. One wheel tub left and a little metal work to tie the floors into the back seat area.  Then All new rear quarters, front right quarter, and front cowl.  That will straighten body up from 50 years of fender benders.  Then the trunk metal, then the front wheel inner fenders." },
    [MODELS_TOPIC_KEY]:{
      images: [
        {url: "P1800_stl_1.png", caption: "The full STL of the Volvo P1800.  All vehicle details", id: "STL1"},
        {url: "P1800_stl_2.png", caption: "Removing the 'glass', wheels and bumpers.", id: "STL2"},
        {url: "P1800_stl_3.png", caption: "Pretty slick model right?", id: "STL3"},
        {url: "P1800_stl_4.png", caption: "Final view of the model from another angle", id: "STL4"},
      ],
      text: "Here is the link to  the Volvo P1800 STL.  It's Free, from me to you.  Use at your own risk.  Enjoy!",
      link:"Volvo_P1800S.stl.zip"
    }
  };


  return (
    <div className={`app ${isMobile && isSidebarOpen ? 'sidebar-open' : ''}`}>
       {isMobile && isSidebarOpen && (
         <div className="sidebar-overlay" onClick={toggleSidebar}></div>
       )}

      <header className="app-header">
        {isMobile && (
          <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle Menu">
            ☰
          </button>
        )}
        <span className="last-updated">Last Updated: {lastUpdated}</span>
        <img src={"/logo.png"} alt="Logo" className="header-logo" />
        <div className="header-links">
          {isLoggedIn ? (
            // Show user info and Logout link if logged in
            <>
              {userProfile && userProfile.picture && (
                 <img src={userProfile.picture} alt="Profile" className="profile-picture" referrerPolicy="no-referrer" />
              )}
              {userProfile && userProfile.name && (
                 <span className="user-name">Hello, {userProfile.name.split(' ')[0]}</span> // Show first name
              )}
              <a href="#logout" onClick={(e) => {e.preventDefault(); handleLogout(); }}>
                Logout
              </a>
            </>
          ) : (
            // Show Google Sign-In button div if not logged in
            // The button will be rendered into this div by the useEffect hook
            <div id="signInDiv" ref={signInDivRef}></div>
          )}
          <a href="mailto:eric.scholz1975@gmail.com">
            Contact Me
          </a>
        </div>
      </header>

      <div className="app-content">
        <aside className={`app-sidebar ${isMobile ? (isSidebarOpen ? 'open' : 'closed') : ''}`}>
          <div className="sidebar-content">
            <h2>Milestones</h2>
            <ul>
              {Object.keys(milestones).map((topic) => (
                <li className="topic-item" key={topic} onClick={() => handleTopicClick(topic)}>
                  {topic}
                </li>
              ))}
            </ul>
            <div className="counter">
              {Array.from({ length: 6 }).map((_, index) => (
                <span key={index} className="cntr">0</span>
              ))}
            </div>
          </div>
        </aside>

        <main className="app-main">
          {!selectedTopic && (
             <div className="main-logo-container">
               <img
                 ref={fullSizeImageRef}
                 src={"/mainlogo.jpg"}
                 alt="Project Overview"
                 className="main-logo-image"
               />
             </div>
          )}

          {selectedTopic && (
            <div>
              <h2>{selectedTopic}</h2>
              {milestones[selectedTopic]?.text && <p>{milestones[selectedTopic].text}</p>}

              {selectedTopic === MODELS_TOPIC_KEY && milestones[selectedTopic]?.link && (
                <button onClick={handleDownload} className="download-button">Download STL</button>
              )}

              <div className="image-grid">
                 {milestones[selectedTopic]?.images && milestones[selectedTopic].images.length > 0 ? (
                     milestones[selectedTopic].images.map((image) => {
                        // *** Add console logs here to inspect values ***
                        // console.log(`Rendering image with id: "${image.id}"`);
                        const commentsForThisImage = allComments.filter(comment => {
                            // console.log(`  Comparing comment.imageId: "${comment.imageId}" with image.id: "${image.id}"`);
                            // Standardize image.id to lowercase for comparison consistency
                            const standardizedImageId = image.id.toLowerCase();
                            return comment.imageId === standardizedImageId;
                        });
                        // console.log(`  Found ${commentsForThisImage.length} comments for image id: "${image.id}"`);

                        return (
                           <div key={image.id} className="image-item">
                               <img
                                   src={image.url}
                                   alt={image.caption || selectedTopic}
                                   onClick={() => handleImageClick(image.url)}
                                   // Corrected comparison here - removed Ref
                                   className={`clickable-image ${selectedImage === image.url ? 'special-image' : ''}`}
                               />
                           {image.caption && <p className="image-caption">{image.caption}</p>}

                           {/* Like Button (commented out) */}
                           {/*
                           <button onClick={() => handleLike(image.id)}>
                               Like ({likes[image.id] || 0})
                           </button>
                           */}
                           {/* Render CommentSection and pass isLoggedIn prop */}
                           <CommentSection
                               imageId={image.id} // Pass the image ID
                               // Pass a function that wraps the API call
                               onCommentSubmit={(commentText) => handleAppCommentSubmit(image.id, commentText)}
                               // Filter all comments to only show comments for this image
                               // This filters the flat allComments array where comment.imageId matches image.id
                               existingComments={commentsForThisImage} // Pass the filtered array
                               isLoggedIn={isLoggedIn} // Pass isLoggedIn prop correctly
                           />
                           </div>
                        );
                     })
                ) : (
                    <p>No images available for this topic.</p>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {selectedImage && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close-button" onClick={closeModal} aria-label="Close image viewer">
              &times;
            </span>
            <img
              src={selectedImage}
              alt="Full Size View"
              className="modal-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
